import { useCallback, useEffect, useState } from "react";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Flex, Icon, Icons, Spin, Switch, useToast } from "@ir/ui";
import { Permissions } from "@src/domain/permissions/Permissions";
import { UpdateBrandPurchaserStatusDTO } from "@src/domain/dto/brands";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { BrandRepository } from "@src/infrastructure/repositories/brand";
import { UpdateBrandPurchaserStatusUseCase } from "@src/application/useCases/brand";
import {
  PurchaserBrandAssociation,
  type IPurchaserBrandAssociation,
} from "@src/domain/entities/purchaser";

interface BrandAssociationStatusToggleProps {
  purchaserId: number;
  association: PurchaserBrandAssociation;
  onAssociationUpdated: (association: PurchaserBrandAssociation) => void;
}

export const BrandAssociationStatusToggle = ({
  purchaserId,
  association,
  onAssociationUpdated,
}: BrandAssociationStatusToggleProps) => {
  const [pendingActive, setPendingActive] = useState(association.isActive);
  const [isUpdating, setIsUpdating] = useState(false);
  const { error: errorToast } = useToast();

  useEffect(() => {
    setPendingActive(association.isActive);
  }, [association.isActive]);

  const handleSwitchChange = useCallback(
    async (mode: boolean) => {
      setPendingActive(mode);
      setIsUpdating(true);
      try {
        const validated = UpdateBrandPurchaserStatusDTO.validate({
          brandId: association.brandId,
          purchaserId,
          isActive: mode,
        });
        if (!validated.success) {
          throw new Error("Validation failed");
        }

        await new UpdateBrandPurchaserStatusUseCase(
          new BrandRepository(GraphQlAdapter.getInstance().getClient()),
        ).execute(new UpdateBrandPurchaserStatusDTO(validated.data));

        const updatedProps: IPurchaserBrandAssociation = {
          brandId: association.brandId,
          brandName: association.brandName,
          brandKey: association.brandKey,
          brandPurchaserId: association.brandPurchaserId,
          purchaserInboxEmailId: association.purchaserInboxEmailId,
          isActive: mode,
        };
        onAssociationUpdated(PurchaserBrandAssociation.from(updatedProps));
      } catch (error) {
        console.error("Failed to update brand association status", error);
        setPendingActive(association.isActive);
        errorToast({
          content: "Failed to update association status. Please try again.",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [association, purchaserId, onAssociationUpdated, errorToast],
  );

  return (
    <HasAnyPermission requiredPermissions={[Permissions.PURCHASERS_EDIT]}>
      <Flex align="center" gap={0.5}>
        {isUpdating && <Spin size="small" spinning />}
        <Switch
          size="small"
          isSelected={pendingActive}
          onChange={handleSwitchChange}
          isDisabled={isUpdating}
          aria-label="Brand association status"
        />
      </Flex>
    </HasAnyPermission>
  );
};
